function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import Auth from "../../functions/authenticate";
import { Loader } from "../../components/loaders";
import { INPUT, LABEL } from "../../components/form-styles";
import { PageHeader, Pill, PlunkDropDown, Segmented } from "../../components/analytics-kit";
import Composer, { parseDesign, starterDesign } from "../email-automation/composer";
import { textOf } from "../email-automation/rich-text";
import { SubjectSuggest } from "../email-automation/ai-draft";
import { loadBrandKit } from "../../functions/brand-kit";
import { hasTenantFeature } from "../../functions/tenant-features";
import account from "../../data/account";
import { NotEnabled, trackingText } from "./common";
import Lifecycle from "./lifecycle";
import { api, audienceApi, normalizeLists, qs, errText, isNotEnabled, idStr, num, who, statusLabel, mailboxTone, leadLabel, leadTone, parseMaybe, windowLabel, sequenceLabel, dateOnly, ago } from "./api";

/*
 * New / edit outreach (DS 2026-10-07: "it works the same way an email campaign would work, it's just that it sends out
 * differently"). One page since the 2026-10-09 Outreach redesign (NewOutreach mockup), laid out like the campaign editor
 * (views/email-campaigns/wizard.jsx): a top bar (name, autosave state, "Send me a test", "Submit for review"), three
 * numbered sections on the left and, on the right, a preview of the selected email plus "Before it goes for review" (the
 * old Review step's checks). The record behind it is an Outreach program (/v1/outreach/programs), never a campaign.
 * Routes: /Email/campaigns/outreach/new and /Email/campaigns/outreach/:id/edit (one Route, so the first save's URL
 * replace keeps this instance mounted).
 *
 *   1 Who it goes to: ONE audience (program.audienceId) as a card; picking one runs the business-only guard
 *                      (POST programs/{id}/estimate {audienceId}) and loads the leads straight away
 *                      (POST programs/{id}/leads, with the 90-day verification attestation when ticked).
 *   2 What it says  : the sequence's emails as rows (Day N, subject, parts still to fill in); a row opens the email
 *                      editor inline (subject, A/B variants, Composer). Waits live inside the editor ("goes out N
 *                      days after the previous email"). Templates (GET templates) from the section header.
 *   3 How it sends  : From (mailboxes), When (send window), a computed pace line, and "More sending options"
 *                      (stop rules, postal address, opt-out line, legal basis, pace and limits, plain text, clicks).
 *
 * Autosave: the program is created (POST programs, or programs/from-template) on the first save with a name, then
 * PUT programs/{id} (settings are replaced whole, so the full form is sent) and PUT steps/{id} for edited steps (the full
 * step every time: PUT applies kind and businessDays even when omitted). The checks (GET lint) and the runway estimate
 * (POST estimate {}) refresh after each save.
 */

const SAVE_DEBOUNCE = 900;
const CHECKS_DEBOUNCE = 1500;
const DAYS = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];
const MERGE = ["first_name", "last_name", "company", "sender_name"];
const lbl = {
  ...LABEL,
  margin: "10px 0 4px"
};
// One control height for every input that sits in a row beside another (date, time, number, select): 32px, same border and padding.
const FIELD = {
  ...INPUT,
  height: 32,
  padding: "0 9px",
  lineHeight: "normal",
  verticalAlign: "middle",
  margin: 0
};
const small = {
  ...FIELD,
  width: 80,
  display: "inline-block"
};
const PLACEHOLDER = /\[\[[^\]]+\]\]/g;
const MERGE_TOKEN = /\{\{\s*([A-Za-z_][\w.]*)\s*(?:\|([^}]*))?\}\}/g;
function freshForm(name) {
  return {
    name: name || "",
    legalBasis: "legitimate_interest",
    audienceId: "",
    window: {
      days: [1, 2, 3, 4, 5],
      start: "08:00",
      end: "17:00",
      tz: "recipient",
      defaultTz: "America/New_York"
    },
    stopRules: {
      onReply: true,
      onClick: false,
      onBounce: true,
      onUnsub: true,
      onOOO: "reschedule",
      onWrongPerson: true
    },
    settings: {
      plainText: true,
      trackClicks: true,
      trackOpens: false,
      jitterMin: 2,
      jitterMax: 9,
      optOutLine: "Reply STOP or use this link to opt out: {url}",
      maxConsumerShare: 0.1
    },
    mailboxIds: []
  };
}
function formFrom(d) {
  const p = d.program || {};
  const base = freshForm(p.name);
  return {
    name: p.name || "",
    legalBasis: p.legalBasis || "legitimate_interest",
    audienceId: p.audienceId != null ? idStr(p.audienceId) : "",
    window: {
      ...base.window,
      ...(d.window || {}),
      days: Array.from(new Set(((d.window || {}).days || base.window.days).map(Number))).sort()
    },
    stopRules: {
      ...base.stopRules,
      ...(d.stopRules || {})
    },
    settings: {
      ...(d.settings || {})
    },
    mailboxIds: (d.mailboxIds || []).map(x => idStr(x))
  };
}
function stepModel(s) {
  return {
    id: idStr(s.id),
    ordinal: Number(s.ordinal),
    kind: s.kind || "email",
    waitDays: s.waitDays == null ? null : Number(s.waitDays),
    businessDays: s.businessDays !== false,
    subject: s.subject || "",
    bodyText: s.bodyText || "",
    designJson: s.designJson || "",
    variants: (parseMaybe(s.variantsJson) || s.variants || []).map(v => ({
      key: v.key || "A",
      subject: v.subject || "",
      bodyText: v.bodyText || "",
      designJson: v.designJson || ""
    }))
  };
}
const esc = t => String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
/** A plain-text step (templates, steps written before 2026-10-08) opened in the Composer: its paragraphs as one text section. */
function textToDesign(text) {
  const paras = String(text || "").replace(/\r\n/g, "\n").split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return {
    frame: "brand",
    version: 1,
    sections: paras.length ? [{
      type: "text",
      html: paras.map(p => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("")
    }] : []
  };
}
function stepBody(s) {
  return {
    ordinal: s.ordinal,
    kind: s.kind,
    waitDays: s.kind === "wait" ? Number(s.waitDays) || 1 : null,
    businessDays: !!s.businessDays,
    subject: s.kind === "email" ? s.subject : null,
    bodyText: s.kind === "email" ? s.bodyText : null,
    designJson: s.kind === "email" ? s.designJson || "" : null,
    variants: s.kind === "email" ? s.variants.filter(v => v.key).map(v => ({
      key: v.key,
      subject: v.subject || null,
      bodyText: v.bodyText || null,
      designJson: v.designJson || null
    })) : []
  };
}
function placeholders(s) {
  const text = [s.subject, parseDesign(s.designJson) ? "" : s.bodyText, s.designJson].concat((s.variants || []).map(v => `${v.subject || ""} ${v.bodyText || ""} ${v.designJson || ""}`)).join(" ");
  return Array.from(new Set(text.match(PLACEHOLDER) || []));
}
/** Template copy stays industry-neutral whatever the catalog says ("showroom visit" → "visit"). */
function neutral(t) {
  if (t == null) return t;
  return String(t).replace(/\bshowroom visits?\b/gi, m => /s$/i.test(m) ? "visits" : "visit").replace(/\bshowrooms?\b/gi, m => /s$/i.test(m) ? "places" : "place");
}
/** "08:00" → "8 AM", "17:30" → "5:30 PM". */
function clock(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  if (!m) return hhmm || "";
  const h = Number(m[1]),
    min = m[2];
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${min === "00" ? "" : `:${min}`} ${h < 12 ? "AM" : "PM"}`;
}
function daysText(days) {
  const d = Array.from(new Set((days || []).map(Number))).sort();
  const key = d.join(",");
  if (key === "1,2,3,4,5") return "Weekdays";
  if (key === "1,2,3,4,5,6,7") return "Every day";
  if (key === "6,7") return "Weekends";
  if (!d.length) return "No days picked";
  return d.map(x => (DAYS.find(y => y[0] === x) || [0, "?"])[1]).join(", ");
}
/** "Weekdays, 8 AM to 5 PM their time". */
function windowText(w) {
  if (!w) return "";
  return `${daysText(w.days)}, ${clock(w.start || "08:00")} to ${clock(w.end || "17:00")} ${w.tz === "recipient" || !w.tz ? "their time" : w.tz}`;
}
/** Merge tokens for display in a row: {{first_name|there}} → {first_name}. */
function tokenText(t) {
  return String(t || "").replace(MERGE_TOKEN, (m, k) => `{${k}}`);
}
/** Merge tokens filled from a lead's values (or the token's fallback, else {field}). */
function fillMerge(t, vals) {
  return String(t || "").replace(MERGE_TOKEN, (m, k, fb) => {
    const v = vals[k] != null && String(vals[k]).trim() ? vals[k] : vals[k.toLowerCase()];
    if (v != null && String(v).trim()) return String(v);
    if (fb != null && fb !== "") return fb;
    return `{${k}}`;
  });
}
/** The email's readable paragraphs: a Composer design's text, or a plain-text body. */
function emailParas(designJson, bodyText) {
  const d = parseDesign(designJson);
  if (!d) return String(bodyText || "").replace(/\r\n/g, "\n").split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  const out = [];
  const html = h => String(h || "").split(/<\/p>|<br\s*\/?>|<\/li>|<\/h\d>|<\/div>/i).map(textOf).filter(Boolean).forEach(p => out.push(p));
  (d.sections || []).forEach(s => {
    switch (s.type) {
      case "text":
      case "footerText":
      case "imageText":
        html(s.html);
        break;
      case "hero":
        if (s.headline) out.push(s.headline);
        if (s.subhead) out.push(s.subhead);
        if (s.label) out.push(`→ ${s.label}`);
        break;
      case "offer":
        if (s.headline) out.push(s.headline);
        html(s.html);
        if (s.code) out.push(s.code);
        if (s.label) out.push(`→ ${s.label}`);
        break;
      case "columns":
        html(s.leftHtml);
        html(s.rightHtml);
        break;
      case "button":
        if (s.label) out.push(`→ ${s.label}`);
        break;
      case "image":
        out.push("(image)");
        break;
      case "products":
      case "recommended":
        out.push("(products)");
        break;
      default:
        break;
    }
  });
  return out;
}
/** Text with [[parts to fill in]] marked amber (shown with single brackets, as the reader would see the gap). */
function marked(text, keyBase) {
  return String(text || "").split(/(\[\[[^\]]+\]\])/).map((part, i) => /^\[\[[^\]]+\]\]$/.test(part) ? /*#__PURE__*/React.createElement("mark", {
    key: `${keyBase}-${i}`,
    className: "orc-ph"
  }, "[", part.slice(2, -2), "]") : /*#__PURE__*/React.createElement(React.Fragment, {
    key: `${keyBase}-${i}`
  }, part));
}

/** B2B guard funnel (LeadLoadFunnel). */
export function LeadFunnel({
  f,
  title,
  onMarkBusiness,
  marking
}) {
  if (!f) return null;
  const rows = [["In the audience", f.members, null], f.audienceTagged ? ["Consumer records kept (prospect list)", f.consumers, null] : ["Not a business contact", f.consumers, "minus"], ["No email address", f.noEmail, "minus"], ["Suppressed (bounced or complained before)", f.suppressed, "minus"], ["Opted out", f.optedOut, "minus"], ["Duplicates", f.duplicates, "minus"], ["Already in this program", f.alreadyInProgram, "minus"]];
  return /*#__PURE__*/React.createElement("div", {
    className: "ec-card or-funnel"
  }, title ? /*#__PURE__*/React.createElement("div", {
    className: "h3"
  }, title) : null, /*#__PURE__*/React.createElement("div", {
    className: "ec-funnel"
  }, rows.map(([l, v, k]) => /*#__PURE__*/React.createElement("div", {
    key: l,
    className: `or-frow ${k || ""}`
  }, /*#__PURE__*/React.createElement("span", null, k ? "− " : "", l), /*#__PURE__*/React.createElement("b", null, k && Number(v) ? `−${num(v)}` : num(v)))), /*#__PURE__*/React.createElement("div", {
    className: "or-frow total"
  }, /*#__PURE__*/React.createElement("span", null, "Leads"), /*#__PURE__*/React.createElement("b", null, f.refused ? "0" : num(f.loaded)))), /*#__PURE__*/React.createElement("div", {
    className: "mini"
  }, num(f.business), " business \xB7 ", num(f.consumers), " consumer", f.audienceTagged ? " · audience tagged for outreach" : "", " \xB7 verified ", num(f.verified), " \xB7 unverified ", num(f.unverified), " \xB7 computed ", ago(f.computedDate)), f.refused ? /*#__PURE__*/React.createElement("div", {
    className: "wa-notice danger",
    style: {
      marginTop: 8
    }
  }, /*#__PURE__*/React.createElement("b", null, "Refused:"), " ", f.refusedReason || "consumer-heavy audience", onMarkBusiness ? /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 8,
      display: "flex",
      gap: 10,
      alignItems: "center",
      flexWrap: "wrap"
    }
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: !!marking,
    onClick: onMarkBusiness
  }, marking ? "Marking…" : "Mark as a business prospect list"), /*#__PURE__*/React.createElement("span", {
    className: "mini"
  }, "For a list of companies you are prospecting, not consumers. Administrator only; recorded in the activity log.")) : null) : null);
}

// Lint text arrives with system words ("set settings.x", "(CAN-SPAM)"): keep the sentence, drop the internals.
function plainFinding(m) {
  return String(m || "").replace(/;?\s*set settings\.[A-Za-z.]+\.?/g, ".").replace(/\s*\((CAN-SPAM|GDPR|CASL)\)/g, "").replace(/\.\.$/, ".").trim();
}
class OutreachWizardComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "reload", async first => {
      try {
        const d = await api("GET", this.path);
        if (!this._alive) return;
        const local = this.state.steps;
        const dirty = this.state.dirtySteps;
        const steps = (d.steps || []).map(stepModel).map(s => dirty[s.id] ? local.find(x => x.id === s.id) || s : s).sort((a, b) => a.ordinal - b.ordinal);
        const next = {
          d,
          steps,
          loadErr: null,
          form: this.state.formDirty && this.state.form ? this.state.form : formFrom(d)
        };
        this.setState(next, first ? this.prefillAddress : undefined);
      } catch (e) {
        if (!this._alive) return;
        if (isNotEnabled(e)) this.setState({
          notEnabled: true
        });else if (first) this.setState({
          loadErr: errText(e, "This outreach could not be loaded.")
        });else this.setState({
          saveErr: errText(e)
        });
      }
    });
    _defineProperty(this, "loadLeads", async () => {
      if (!this.state.programId) return;
      try {
        const r = await api("GET", `${this.path}/leads${qs({
          status: this.state.leadStatus,
          $top: 25
        })}`);
        if (this._alive) this.setState({
          leads: r,
          leadIdx: 0
        });
      } catch (e) {
        if (this._alive) this.setState({
          leads: {
            items: [],
            total: 0,
            err: errText(e)
          }
        });
      }
    });
    // ---------------------------------------------------------------- autosave
    _defineProperty(this, "setForm", (section, patch) => {
      this._rev++;
      this.setState(s => ({
        formDirty: true,
        nameErr: null,
        form: section ? {
          ...s.form,
          [section]: {
            ...s.form[section],
            ...patch
          }
        } : {
          ...s.form,
          ...patch
        }
      }), () => this.queueSave());
    });
    _defineProperty(this, "editStep", (id, patch) => {
      const rev = ++this._rev;
      this.setState(s => ({
        steps: s.steps.map(x => x.id === id ? {
          ...x,
          ...patch
        } : x),
        dirtySteps: {
          ...s.dirtySteps,
          [id]: rev
        }
      }), () => this.queueSave());
    });
    /** The postal address defaults to the brand kit's mailing address (Settings → Brand kit) when the program has none. */
    _defineProperty(this, "prefillAddress", () => {
      const {
        form,
        brand
      } = this.state;
      const addr = brand && brand.mailingAddress ? String(brand.mailingAddress).trim() : "";
      if (!addr || !form || !this.state.programId || this.locked() || (form.settings || {}).physicalAddress) return;
      this.setForm("settings", {
        physicalAddress: addr
      });
    });
    _defineProperty(this, "save", () => {
      clearTimeout(this._saveT);
      if (!this._alive) return Promise.resolve();
      if (this._saving) {
        this._pending = true;
        return this._saving;
      }
      if (this.locked()) return Promise.resolve();
      this._saving = (async () => {
        // Yield first: a save with nothing to write returns before any await, and its finally would clear _saving
        // before this assignment, leaving a settled promise that queues every later save behind it forever.
        await null;
        const {
          programId,
          form,
          template
        } = this.state;
        let wrote = false;
        try {
          if (!programId) {
            const name = form.name.trim();
            if (!name) return;
            this.setState({
              saveState: "saving",
              saveErr: null
            });
            const rev = this._rev;
            const body = {
              name,
              audienceId: form.audienceId || undefined
            };
            const d = template ? await api("POST", "/programs/from-template", {
              key: template,
              ...body
            }) : await api("POST", "/programs", {
              ...body,
              legalBasis: form.legalBasis
            });
            const id = d && d.program && d.program.id || d && d.id;
            if (!id) throw new Error("The program was created but the API returned no id.");
            if (!this._alive) return;
            this.setState({
              programId: id,
              formDirty: this._rev !== rev,
              template: ""
            });
            this.props.history.replace(`/Email/campaigns/outreach/${encodeURIComponent(id)}/edit`);
            await this.reload();
            this.loadLeads();
            if (this.state.form.audienceId) this.previewGuard();
            // An audience picked before the outreach had a name: load its leads now that the program exists.
            if (this._loadAfterCreate && this.state.form.audienceId) {
              this._loadAfterCreate = false;
              setTimeout(() => this._alive && this.loadAudience(), 0);
            }
            wrote = true;
          } else {
            const {
              formDirty,
              dirtySteps,
              steps
            } = this.state;
            const ids = Object.keys(dirtySteps);
            if (!formDirty && !ids.length) return;
            this.setState({
              saveState: "saving",
              saveErr: null
            });
            const audienceBefore = this.state.d && this.state.d.program && this.state.d.program.audienceId != null ? idStr(this.state.d.program.audienceId) : "";
            if (formDirty) {
              const rev = this._rev;
              await api("PUT", this.path, this.formBody());
              if (this._alive && this._rev === rev) this.setState({
                formDirty: false
              });
            }
            for (const sid of ids) {
              const st = steps.find(x => x.id === sid);
              const rev = dirtySteps[sid];
              if (st && !this.stepsLocked()) await api("PUT", `/steps/${sid}`, stepBody(st));
              if (!this._alive) return;
              this.setState(s => {
                const dd = {
                  ...s.dirtySteps
                };
                if (dd[sid] === rev) delete dd[sid];
                return {
                  dirtySteps: dd
                };
              });
            }
            await this.reload();
            if (formDirty && this.state.form.audienceId && this.state.form.audienceId !== audienceBefore) this.previewGuard();
            wrote = true;
          }
          if (this._alive) this.setState({
            saveState: "saved",
            savedAt: new Date()
          });
          if (wrote) this.queueChecks();
        } catch (e) {
          if (this._alive) this.setState({
            saveState: "error",
            saveErr: errText(e, "Autosave failed.")
          });
        } finally {
          this._saving = null;
          if (this._pending && this._alive) {
            this._pending = false;
            this.save();
          }
        }
      })();
      return this._saving;
    });
    _defineProperty(this, "flush", async () => {
      clearTimeout(this._saveT);
      if (this._saving) {
        try {
          await this._saving;
        } catch (e) {/* shown */}
      }
      await this.save();
      if (this._saving) {
        try {
          await this._saving;
        } catch (e) {/* shown */}
      }
    });
    /** Back arrow: flush the autosave, then return to Outreach. */
    _defineProperty(this, "back", async () => {
      try {
        await this.flush();
      } catch (e) {/* autosave errors already show */}
      this.props.history.push("/Email/outreach");
    });
    _defineProperty(this, "runChecks", () => {
      if (!this.state.programId) return;
      this.lint();
      this.estimate();
    });
    /** "Show me" / "Fix it": scroll to a section (or one email row) and flash it. */
    _defineProperty(this, "goTo", (n, el) => {
      const node = el || this.sec[n] && this.sec[n].current;
      if (node && node.scrollIntoView) node.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      clearTimeout(this._flashT);
      this.setState({
        flash: n
      });
      this._flashT = setTimeout(() => this._alive && this.setState({
        flash: null
      }), 1600);
    });
    _defineProperty(this, "togglePanel", (k, on) => this.setState(s => ({
      panel: {
        ...s.panel,
        [k]: on === undefined ? !s.panel[k] : on
      }
    })));
    // ---------------------------------------------------------------- 1 · who it goes to
    _defineProperty(this, "previewGuard", async () => {
      const {
        form,
        programId
      } = this.state;
      if (!form.audienceId || !programId) return;
      this.setState({
        preview: {
          busy: true
        }
      });
      try {
        const est = await api("POST", `${this.path}/estimate`, {
          audienceId: form.audienceId
        });
        if (this._alive) this.setState({
          preview: {
            funnel: est && est.funnel,
            warnings: est && est.warnings || [],
            for: form.audienceId
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          preview: {
            err: errText(e, "The audience check failed.")
          }
        });
      }
    });
    // Prospect lists have no customer rows, so the business-only guard refuses them until an Administrator marks the
    // audience as a business list (POST audiences/{id}/business-list adds the "outreach" tag); then re-check, and load
    // the leads when the check now passes.
    _defineProperty(this, "markBusiness", async () => {
      const {
        form,
        preview
      } = this.state;
      if (!form.audienceId) return;
      this.setState({
        preview: {
          ...preview,
          marking: true
        }
      });
      try {
        await api("POST", `/audiences/${form.audienceId}/business-list`, {
          business: true
        }, {
          admin: true
        });
        await this.previewGuard();
        const f = this.state.preview.funnel;
        if (this._alive && f && !f.refused) this.loadAudience();
      } catch (e) {
        if (this._alive) this.setState({
          preview: {
            ...this.state.preview,
            marking: false,
            err: errText(e, "The audience could not be marked.")
          }
        });
      }
    });
    _defineProperty(this, "loadAudience", async () => {
      await this.flush();
      const {
        form,
        attest
      } = this.state;
      if (!form.audienceId || !this.state.programId) return;
      this.setState({
        load: {
          busy: true
        }
      });
      const body = {
        audienceId: form.audienceId
      };
      if (attest.on) body.verification = {
        attested: true,
        date: new Date(attest.date + "T12:00:00Z").toISOString(),
        verifier: attest.verifier.trim() || undefined
      };
      try {
        const f = await api("POST", `${this.path}/leads`, body);
        if (!this._alive) return;
        this.setState({
          load: {
            funnel: f,
            ok: true
          }
        });
        await this.reload();
        this.loadLeads();
        this.queueChecks();
      } catch (e) {
        if (this._alive) this.setState({
          load: {
            err: errText(e, "The leads could not be loaded."),
            funnel: e.data && e.data.funnel
          }
        });
      }
    });
    /** An audience was picked: save it and load its leads straight away (once the outreach has a name). */
    _defineProperty(this, "pickAudience", id => {
      this.setState({
        load: {},
        preview: {},
        panel: {
          ...this.state.panel,
          picker: false
        },
        listQ: ""
      });
      this.setForm(null, {
        audienceId: id
      });
      if (!id) return;
      if (this.state.programId) setTimeout(() => this._alive && this.loadAudience(), 0);else {
        this._loadAfterCreate = true;
        if (!this.state.form.name.trim()) this.setState({
          nameErr: "Name this outreach so it can be saved; the leads load as soon as it is."
        });
      }
    });
    _defineProperty(this, "removeLead", lead => async () => {
      this.setState({
        leadBusy: idStr(lead.id)
      });
      try {
        await api("DELETE", `${this.path}/leads`, {
          personIds: [idStr(lead.personId)]
        });
        this.setState({
          leadBusy: null
        });
        this.reload();
        this.loadLeads();
      } catch (e) {
        this.setState({
          leadBusy: null,
          leads: {
            ...this.state.leads,
            err: errText(e, "The lead could not be removed.")
          }
        });
      }
    });
    _defineProperty(this, "attestVerified", async () => {
      const {
        attest
      } = this.state;
      this.setState({
        verify: {
          busy: true
        }
      });
      try {
        const r = await api("POST", "/leads/verify", {
          programId: this.state.programId,
          attestation: {
            attested: true,
            date: new Date(attest.date + "T12:00:00Z").toISOString(),
            verifier: attest.verifier.trim() || undefined
          }
        });
        this.setState({
          verify: {
            res: r
          }
        });
        this.reload();
        this.loadLeads();
      } catch (e) {
        this.setState({
          verify: {
            err: errText(e, "The attestation failed.")
          }
        });
      }
    });
    // ---------------------------------------------------------------- 2 · what it says (sequence)
    /** Template on an existing program with no steps yet: add its steps (POST programs/{id}/steps, in order). */
    _defineProperty(this, "applyTemplate", async () => {
      const t = (this.state.templates || []).find(x => x.key === this.state.template);
      if (!t) return;
      this.setState({
        applyBusy: true,
        applyErr: null
      });
      try {
        for (const s of t.steps || []) await api("POST", `${this.path}/steps`, {
          ordinal: s.ordinal,
          kind: s.kind,
          waitDays: s.waitDays,
          businessDays: s.businessDays !== false,
          subject: neutral(s.subject),
          bodyText: neutral(s.bodyText),
          variants: s.variants || undefined
        });
        this.setState({
          applyBusy: false,
          template: ""
        });
        await this.reload();
        this.queueChecks();
      } catch (e) {
        this.setState({
          applyBusy: false,
          applyErr: errText(e, "The template could not be applied.")
        });
        await this.reload();
      }
    });
    /** Open the first email's editor inline and scroll to it. */
    _defineProperty(this, "openFirstEmail", () => {
      const r = this.emailRows()[0];
      if (r) this.setState({
        open: r.s.id,
        previewId: r.s.id
      }, () => this.goTo(2, document.getElementById(`orc-step-${r.s.id}`)));
    });
    /**
     * A template card or Blank. With no outreach yet: name it (the typed name, else "New outreach, <Mon D>"), save (a template
     * rides on POST programs/from-template), then open the first email. Blank on an empty outreach adds the first email
     * (subject and body ready to type). On a saved outreach a template adds its steps.
     */
    _defineProperty(this, "pickTemplate", async key => {
      this.togglePanel("templates", false);
      if (this.stepsLocked()) return;
      if (!this.state.programId) {
        const name = this.state.form.name.trim() || this.autoName();
        this._rev++;
        await new Promise(r => this.setState(s => ({
          template: key,
          nameErr: null,
          form: {
            ...s.form,
            name
          }
        }), r));
        await this.flush();
        if (!this._alive || !this.state.programId) return; // the save error shows in the top bar
        if (key) {
          this.openFirstEmail();
          return;
        }
      } else if (key) {
        this.setState({
          template: key
        }, async () => {
          await this.applyTemplate();
          if (this._alive) this.openFirstEmail();
        });
        return;
      }
      if (!this.state.steps.some(x => x.kind === "email")) this.addStep("email")();else this.openFirstEmail();
    });
    /** POST programs/{id}/steps for each body, in order. */
    _defineProperty(this, "addSteps", async bodies => {
      this.setState({
        stepBusy: "add",
        stepErr: null
      });
      try {
        await this.flush();
        for (const b of bodies) await api("POST", `${this.path}/steps`, b);
        this.setState({
          stepBusy: null
        });
        const before = new Set(this.state.steps.map(s => s.id));
        await this.reload();
        const added = this.state.steps.find(s => s.kind === "email" && !before.has(s.id));
        if (added) this.setState({
          open: added.id,
          previewId: added.id
        });
        this.queueChecks();
      } catch (e) {
        this.setState({
          stepBusy: null,
          stepErr: errText(e, "The email could not be added.")
        });
        await this.reload();
      }
    });
    _defineProperty(this, "addStep", kind => () => this.addSteps([kind === "wait" ? {
      kind: "wait",
      waitDays: 3,
      businessDays: true
    } : this.emailBody(!this.state.steps.some(s => s.kind === "email"))]));
    /** "+ Add a follow-up": a wait (3 business days) then an email in the same thread. */
    _defineProperty(this, "addFollowUp", () => {
      const hasEmail = this.state.steps.some(s => s.kind === "email");
      this.addSteps(hasEmail ? [{
        kind: "wait",
        waitDays: 3,
        businessDays: true
      }, this.emailBody(false)] : [this.emailBody(true)]);
    });
    /** Delete an email and the wait that leads into it (for the first email, the wait after it), so the days stay right. */
    _defineProperty(this, "deleteEmail", st => async () => {
      const {
        steps
      } = this.state;
      const i = steps.findIndex(x => x.id === st.id);
      if (i < 0) return;
      const prevEmail = steps.slice(0, i).map(x => x.kind).lastIndexOf("email");
      let waits = [];
      if (prevEmail > -1) waits = steps.slice(prevEmail + 1, i).filter(x => x.kind === "wait");else {
        for (let k = i + 1; k < steps.length && steps[k].kind === "wait"; k++) waits.push(steps[k]);
      }
      this.setState({
        stepBusy: st.id,
        stepErr: null
      });
      try {
        for (const x of [st].concat(waits)) {
          await api("DELETE", `/steps/${x.id}`);
          this.setState(s => {
            const dd = {
              ...s.dirtySteps
            };
            delete dd[x.id];
            return {
              dirtySteps: dd
            };
          });
        }
        this.setState({
          stepBusy: null,
          open: null,
          previewId: this.state.previewId === st.id ? null : this.state.previewId
        });
        await this.reload();
        this.queueChecks();
      } catch (e) {
        this.setState({
          stepBusy: null,
          stepErr: errText(e, "The email could not be deleted.")
        });
        await this.reload();
      }
    });
    /** Swap two steps' ordinals through a free temporary ordinal (ordinals are unique per program). */
    _defineProperty(this, "swapSteps", (a, other) => async () => {
      await this.flush();
      const {
        steps
      } = this.state;
      const temp = Math.max(...steps.map(x => x.ordinal)) + 100;
      this.setState({
        stepBusy: a.id,
        stepErr: null
      });
      try {
        await api("PUT", `/steps/${a.id}`, {
          ...stepBody(a),
          ordinal: temp
        });
        await api("PUT", `/steps/${other.id}`, {
          ...stepBody(other),
          ordinal: a.ordinal
        });
        await api("PUT", `/steps/${a.id}`, {
          ...stepBody(a),
          ordinal: other.ordinal
        });
        this.setState({
          stepBusy: null
        });
      } catch (e) {
        this.setState({
          stepBusy: null,
          stepErr: errText(e, "The emails could not be reordered.")
        });
      }
      await this.reload();
    });
    _defineProperty(this, "openEmail", id => this.setState(s => ({
      open: s.open === id ? null : id,
      previewId: id
    })));
    /** "Show me": open the first email with parts to fill in and scroll to it. */
    _defineProperty(this, "showPlaceholders", () => {
      const row = this.emailRows().find(r => placeholders(r.s).length);
      if (!row) {
        this.goTo(2);
        return;
      }
      this.setState({
        open: row.s.id,
        previewId: row.s.id
      }, () => this.goTo(2, document.getElementById(`orc-step-${row.s.id}`)));
    });
    /** Editor preview: POST programs/{id}/render composes the email exactly as a send (brand frame, sample lead, signature, footer). */
    _defineProperty(this, "renderPreviewFor", (s, subject) => design => api("POST", `${this.path}/render`, {
      design,
      subject: subject || s.subject || null,
      stepOrdinal: s.ordinal
    }));
    // ---------------------------------------------------------------- 3 · how it sends
    _defineProperty(this, "toggleMailbox", id => {
      const ids = this.state.form.mailboxIds;
      this.setForm(null, {
        mailboxIds: ids.indexOf(id) > -1 ? ids.filter(x => x !== id) : ids.concat([id])
      });
    });
    // ---------------------------------------------------------------- checks, test, submit
    _defineProperty(this, "lint", async () => {
      this.setState({
        lint: {
          ...this.state.lint,
          busy: true,
          err: null
        }
      });
      try {
        const r = await api("GET", `${this.path}/lint`);
        if (this._alive) this.setState({
          lint: {
            res: r
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          lint: {
            err: errText(e, "The checks could not run.")
          }
        });
      }
    });
    _defineProperty(this, "estimate", async () => {
      this.setState({
        est: {
          ...this.state.est,
          busy: true,
          err: null
        }
      });
      try {
        const r = await api("POST", `${this.path}/estimate`, {});
        if (this._alive) this.setState({
          est: {
            res: r
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          est: {
            err: errText(e, "The estimate could not run.")
          }
        });
      }
    });
    _defineProperty(this, "sendTest", async () => {
      const {
        test
      } = this.state;
      if (!test.to || test.to.indexOf("@") < 0) {
        this.setState({
          test: {
            ...test,
            open: true,
            err: "Enter the address to send the test to."
          }
        });
        return;
      }
      await this.flush();
      this.setState({
        test: {
          ...this.state.test,
          busy: true,
          err: null,
          res: null,
          preview: null
        }
      });
      const body = {
        to: test.to.trim()
      };
      if (test.stepOrdinal) body.stepOrdinal = Number(test.stepOrdinal);
      if (test.mailboxId) body.mailboxId = idStr(test.mailboxId);
      try {
        const r = await api("POST", `${this.path}/test-send`, body);
        this.setState({
          test: {
            ...this.state.test,
            busy: false,
            res: r
          }
        });
        this.reload();
      } catch (e) {
        this.setState({
          test: {
            ...this.state.test,
            busy: false,
            err: errText(e, "The test send failed."),
            preview: e.data && e.data.preview
          }
        });
      }
    });
    /** "Send me a test" (top bar): the email in the preview, to the signed-in address. */
    _defineProperty(this, "sendTestToMe", () => {
      const me = account.getEmail && account.getEmail() || "";
      const row = this.previewRow();
      const stepOrdinal = row && row.index > 0 ? String(row.s.ordinal) : "";
      if (!me) {
        this.setState({
          test: {
            ...this.state.test,
            open: true,
            stepOrdinal,
            err: "Your address is not known; type one."
          }
        }, () => this.goTo("checks"));
        return;
      }
      this.setState({
        test: {
          ...this.state.test,
          to: me,
          stepOrdinal
        }
      }, this.sendTest);
    });
    /** Submit for review (POST programs/{id}/submit), offered only when the API lists InReview as a next transition. */
    _defineProperty(this, "submit", async () => {
      const {
        d
      } = this.state;
      if (!d || !d.program) return;
      await this.flush();
      this.setState({
        submit: {
          busy: true
        }
      });
      try {
        const r = await api("POST", `/programs/${encodeURIComponent(d.program.id)}/submit`);
        if (!this._alive) return;
        this.setState({
          submit: {
            ok: r && r.status ? `Now ${statusLabel(r.status).toLowerCase()}.` : "Submitted for review."
          }
        });
        await this.reload();
        this.runChecks();
      } catch (e) {
        if (this._alive) this.setState({
          submit: {
            err: errText(e, "That action failed.")
          }
        }, () => this.goTo("checks"));
      }
    });
    const _st = props.location && props.location.state || {};
    const _id = props.params && props.params.id ? decodeURIComponent(props.params.id) : null;
    this.state = {
      programId: _id,
      d: null,
      form: _id ? null : freshForm(_st.name),
      steps: [],
      dirtySteps: {},
      formDirty: false,
      loadErr: null,
      notEnabled: false,
      saveState: "idle",
      saveErr: null,
      savedAt: null,
      nameErr: null,
      lists: null,
      mailboxes: null,
      templates: null,
      template: "",
      applyBusy: false,
      applyErr: null,
      listQ: "",
      preview: {},
      attest: {
        on: false,
        verifier: "",
        date: new Date().toISOString().slice(0, 10)
      },
      load: {},
      leads: null,
      leadStatus: "all",
      leadBusy: null,
      verify: {},
      stepBusy: null,
      stepErr: null,
      lint: {},
      est: {},
      brand: null,
      editing: {},
      test: {
        open: false,
        to: account.getEmail && account.getEmail() || "",
        stepOrdinal: "",
        mailboxId: ""
      },
      // one-page UI
      open: null,
      previewId: null,
      leadIdx: 0,
      panel: {},
      flash: null,
      submit: {}
    };
    this.sec = {
      1: /*#__PURE__*/React.createRef(),
      2: /*#__PURE__*/React.createRef(),
      3: /*#__PURE__*/React.createRef(),
      checks: /*#__PURE__*/React.createRef()
    };
    this._alive = true;
    this._rev = 0;
    this._loadAfterCreate = false;
  }
  get path() {
    return `/programs/${encodeURIComponent(this.state.programId)}`;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    audienceApi("GET", "").then(d => this._alive && this.setState({
      lists: normalizeLists(d)
    })).catch(() => this._alive && this.setState({
      lists: []
    }));
    api("GET", "/mailboxes").then(m => this._alive && this.setState({
      mailboxes: Array.isArray(m) ? m : []
    })).catch(e => this._alive && this.setState({
      mailboxes: [],
      notEnabled: isNotEnabled(e)
    }));
    loadBrandKit().then(b => {
      if (this._alive) this.setState({
        brand: b || {}
      }, this.prefillAddress);
    }).catch(() => {});
    api("GET", "/templates").then(t => this._alive && this.setState({
      templates: Array.isArray(t) ? t : t && t.items || []
    })).catch(() => this._alive && this.setState({
      templates: []
    }));
    if (this.state.programId) {
      await this.reload(true);
      this.loadLeads();
      if (this.state.d && this.state.form && this.state.form.audienceId && !(this.state.d.leads || {}).total) this.previewGuard();
      if (this.state.d) this.runChecks();
    } else if (this.state.form.name.trim()) this.queueSave();
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._saveT);
    clearTimeout(this._checksT);
    clearTimeout(this._flashT);
  }
  status() {
    return ((this.state.d || {}).program || {}).status || "Draft";
  }
  locked() {
    const s = this.status();
    return s === "Completed" || s === "Canceled" || s === "InReview";
  }
  stepsLocked() {
    return this.locked() || this.status() === "Running";
  }
  queueSave() {
    if (this.locked()) return;
    clearTimeout(this._saveT);
    this._saveT = setTimeout(() => this.save(), SAVE_DEBOUNCE);
  }
  formBody() {
    const {
      form
    } = this.state;
    const settings = {
      ...form.settings
    };
    ["jitterMin", "jitterMax"].forEach(k => {
      if (settings[k] === "" || settings[k] == null) delete settings[k];else settings[k] = Number(settings[k]);
    });
    ["pauseBounceRate", "maxConsumerShare"].forEach(k => {
      if (settings[k] === "" || settings[k] == null) delete settings[k];else settings[k] = Number(settings[k]);
    });
    return {
      name: form.name.trim() || undefined,
      legalBasis: form.legalBasis,
      audienceId: form.audienceId || undefined,
      window: form.window,
      stopRules: form.stopRules,
      settings,
      mailboxIds: form.mailboxIds.map(x => idStr(x))
    };
  }
  /** The checklist's server checks (GET lint) and the runway (POST estimate {}), refreshed after saves. */
  queueChecks() {
    clearTimeout(this._checksT);
    this._checksT = setTimeout(() => this._alive && this.runChecks(), CHECKS_DEBOUNCE);
  }
  /** The funnel behind the audience card: the last load, else the guard check. */
  funnel() {
    const {
      load,
      preview
    } = this.state;
    return load.funnel || preview.funnel || null;
  }
  renderWho() {
    const {
      d,
      form,
      programId,
      lists,
      listQ,
      preview,
      attest,
      load,
      leads,
      leadStatus,
      leadBusy,
      verify,
      panel
    } = this.state;
    const counts = d && d.leads || {};
    const v = d && (d.settings || {}).verification;
    const locked = this.locked();
    const chosen = (lists || []).find(l => l.id === form.audienceId);
    const f = this.funnel();
    const refused = f && f.refused ? f : preview.funnel && preview.funnel.refused ? preview.funnel : null;
    const q = listQ.trim().toLowerCase();
    const rows = (lists || []).filter(l => !q || `${l.name} ${l.kind}`.toLowerCase().includes(q)).slice(0, 40);
    const statusOpts = [["all", "Status: all"]].concat(["queued", "in_sequence", "replied", "interested", "not_interested", "ooo", "bounced", "unsubscribed", "completed", "stopped"].map(s => [s, leadLabel(s)]));
    const busy = load.busy || preview.busy;
    let note;
    if (busy) note = load.busy ? "Loading the leads…" : "Checking who can get it…";else if (f && !f.refused) note = `${num(f.business)} business contact${Number(f.business) === 1 ? "" : "s"} · ${Number(f.noEmail) ? `${num(f.noEmail)} without an email are skipped` : "all have a work email"}`;else if (counts.total) note = `${num(counts.total)} leads loaded · ${num(counts.verified)} checked, ${num(counts.unverified)} not checked`;else if (chosen) note = `${chosen.kind ? `${chosen.kind} list` : "List"}${chosen.total ? ` of ${num(chosen.total)}` : ""}`;else note = form.audienceId ? `Audience ${form.audienceId}` : "";
    const picker = !locked && (panel.picker || !form.audienceId) ? /*#__PURE__*/React.createElement("div", {
      className: "ec-picker orc-picker"
    }, /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      value: listQ,
      placeholder: "Search audiences",
      "aria-label": "Search audiences",
      autoFocus: !!panel.picker,
      onChange: e => this.setState({
        listQ: e.target.value
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "ec-picker-list"
    }, !lists ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "Loading audiences\u2026") : !rows.length ? /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, "No audience matches.") : rows.map(l => /*#__PURE__*/React.createElement("button", {
      key: l.id,
      type: "button",
      className: l.id === form.audienceId ? "on" : "",
      onClick: () => this.pickAudience(l.id)
    }, /*#__PURE__*/React.createElement("span", null, l.id === form.audienceId ? "✓ " : "", l.name), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, l.kind, l.total ? ` · ${num(l.total)}` : "")))), /*#__PURE__*/React.createElement("div", {
      className: "ec-picker-foot"
    }, "One audience at a time; to reach several lists, combine them into one audience. ", /*#__PURE__*/React.createElement(Link, {
      to: "/Audiences/this_month"
    }, "Manage audiences"), form.audienceId ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: () => this.setState({
        panel: {
          ...panel,
          picker: false
        }
      })
    }, "Keep ", chosen ? chosen.name : "this audience")) : null)) : null;
    return /*#__PURE__*/React.createElement(React.Fragment, null, form.audienceId ? /*#__PURE__*/React.createElement("div", {
      className: "orc-audcard"
    }, /*#__PURE__*/React.createElement("div", {
      className: "orc-audcard-text"
    }, /*#__PURE__*/React.createElement("b", null, chosen ? chosen.name : `Audience ${form.audienceId}`), /*#__PURE__*/React.createElement("span", null, note)), !locked && programId && !busy ? counts.total ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn small",
      title: "Load members who joined the audience since the last load",
      onClick: this.loadAudience
    }, "Add anyone new") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn small",
      onClick: this.loadAudience
    }, "Load leads") : null, !locked ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn",
      onClick: () => this.togglePanel("picker")
    }, "Change") : null) : /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Pick the audience to reach. Only business contacts with a work email become leads; a list that is mostly consumers is refused."), picker, !programId && form.audienceId && !form.name.trim() ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note warn"
    }, "Name this outreach (top left) to save it; the leads load as soon as it is saved.") : null, refused ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger orc-notice"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Refused:"), " ", refused.refusedReason || "This audience is mostly consumers. Outreach only sends to business contacts."), !locked ? /*#__PURE__*/React.createElement("span", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: !!preview.marking,
      onClick: this.markBusiness
    }, preview.marking ? "Marking…" : "Mark as a business prospect list"), /*#__PURE__*/React.createElement("span", {
      className: "mini"
    }, "For a list of companies you are prospecting, not consumers. Administrator only; recorded in the activity log.")) : null) : load.err ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger orc-notice"
    }, load.err) : null, preview.err ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger orc-notice"
    }, preview.err) : null, (preview.warnings || []).length ? /*#__PURE__*/React.createElement("ul", {
      className: "ec-notes"
    }, preview.warnings.map((w, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, w))) : null, /*#__PURE__*/React.createElement("label", {
      className: "ecc-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: attest.on,
      disabled: locked,
      onChange: e => this.setState({
        attest: {
          ...attest,
          on: e.target.checked
        }
      })
    }), "These addresses were checked in the last 90 days"), attest.on ? /*#__PURE__*/React.createElement("div", {
      className: "orc-inline orc-attest"
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        ...FIELD,
        width: 220
      },
      placeholder: "Checked by (tool or person)",
      "aria-label": "Checked by",
      value: attest.verifier,
      onChange: e => this.setState({
        attest: {
          ...attest,
          verifier: e.target.value
        }
      })
    }), /*#__PURE__*/React.createElement("input", {
      style: {
        ...FIELD,
        width: 160
      },
      type: "date",
      "aria-label": "Checked on",
      value: attest.date,
      onChange: e => this.setState({
        attest: {
          ...attest,
          date: e.target.value
        }
      })
    }), counts.unverified > 0 && !locked ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      disabled: verify.busy,
      onClick: this.attestVerified
    }, verify.busy ? "Marking…" : `Mark the ${num(counts.unverified)} loaded leads as checked`) : null) : null, /*#__PURE__*/React.createElement("span", {
      className: "ecc-note"
    }, attest.on ? "Leads loaded from now on are marked as checked." : "If not, the first emails from each mailbox are watched closely and paused if too many bounce."), verify.err ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note bad"
    }, verify.err) : verify.res ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, verify.res.note, " (", num(verify.res.marked), " marked)") : null, v && v.attested ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Marked as checked ", v.date ? dateOnly(v.date) : "", v.verifier ? ` by ${v.verifier}` : "", v.by ? ` (recorded by ${v.by})` : "", ".") : null, programId && (counts.total || f) ? /*#__PURE__*/React.createElement("div", {
      className: "orc-disclosures"
    }, counts.total ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-disclosure",
      "aria-expanded": !!panel.leads,
      onClick: () => this.togglePanel("leads")
    }, panel.leads ? "▾" : "▸", " See the leads") : null, f ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-disclosure",
      "aria-expanded": !!panel.funnel,
      onClick: () => this.togglePanel("funnel")
    }, panel.funnel ? "▾" : "▸", " Who was left out and why") : null) : null, panel.funnel && f ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-breakdown"
    }, /*#__PURE__*/React.createElement(LeadFunnel, {
      f: f,
      title: load.funnel ? "Last load" : "Who becomes a lead"
    })) : null, panel.leads && counts.total ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-breakdown orc-leads"
    }, /*#__PURE__*/React.createElement("div", {
      className: "or-counts"
    }, [["Total", counts.total], ["Active", counts.active], ["Checked", counts.verified], ["Not checked", counts.unverified], ["Replied", counts.replied], ["Interested", counts.interested], ["Bounced", counts.bounced], ["Unsubscribed", counts.unsubscribed]].map(([l, n]) => /*#__PURE__*/React.createElement("span", {
      key: l,
      className: "wa-chip"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, l), " ", num(n)))), /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        margin: "6px 0"
      }
    }, /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: statusOpts,
      value: leadStatus,
      onChange: x => this.setState({
        leadStatus: x
      }, this.loadLeads),
      width: "170px"
    }), leads ? /*#__PURE__*/React.createElement("span", {
      className: "mini"
    }, num((leads.items || []).length), " of ", num(leads.total), " shown") : null), leads && leads.err ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, leads.err) : null, leads && (leads.items || []).length ? /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table ec-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Lead"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Checked"), /*#__PURE__*/React.createElement("th", {
      className: "ec-row-actions"
    }))), /*#__PURE__*/React.createElement("tbody", null, leads.items.map(l => {
      const m = parseMaybe(l.mergeJson) || {};
      return /*#__PURE__*/React.createElement("tr", {
        key: idStr(l.id)
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, l.email), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, [m.first_name, m.last_name].filter(Boolean).join(" "), m.company ? ` · ${m.company}` : "")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
        tone: leadTone(l.status)
      }, leadLabel(l.status))), /*#__PURE__*/React.createElement("td", {
        className: "dim"
      }, l.verifyStatus || "unverified"), /*#__PURE__*/React.createElement("td", {
        className: "ec-row-actions"
      }, locked ? null : /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle danger",
        disabled: leadBusy === idStr(l.id),
        onClick: this.removeLead(l)
      }, leadBusy === idStr(l.id) ? "…" : "Remove")));
    })))) : null) : null);
  }
  /** "New outreach, Oct 9": the name a template or Blank gives an outreach nobody has named yet (editable in the top bar). */
  autoName() {
    return `New outreach, ${new Date().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    })}`;
  }
  emailBody(isFirst) {
    return {
      kind: "email",
      businessDays: true,
      subject: isFirst ? "Quick question about {{company|your team}}" : "",
      bodyText: isFirst ? "Hi {{first_name|there}},\n\n\n\nWorth 15 minutes?\n\n{{sender_name}}" : "Hi {{first_name|there}},\n\n\n\n{{sender_name}}"
    };
  }
  /** Email steps with their send day and the waits that lead into each. */
  emailRows() {
    const out = [];
    let day = 0,
      waits = [];
    this.state.steps.forEach(s => {
      if (s.kind === "wait") {
        day += Number(s.waitDays) || 0;
        waits.push(s);
        return;
      }
      out.push({
        s,
        day,
        waits,
        index: out.length
      });
      waits = [];
    });
    return out;
  }
  renderSays() {
    const {
      steps,
      dirtySteps,
      stepBusy,
      stepErr,
      templates,
      template,
      programId,
      open,
      previewId,
      applyBusy,
      applyErr,
      panel
    } = this.state;
    const locked = this.stepsLocked();
    const rows = this.emailRows();
    const chosenT = template ? (templates || []).find(t => t.key === template) : null;
    return /*#__PURE__*/React.createElement(React.Fragment, null, panel.templates && !locked ? /*#__PURE__*/React.createElement("div", {
      className: "orc-tpicker",
      role: "listbox",
      "aria-label": "Templates"
    }, (templates || []).map(t => /*#__PURE__*/React.createElement("button", {
      key: t.key,
      type: "button",
      role: "option",
      "aria-selected": template === t.key,
      className: template === t.key ? "on" : "",
      onClick: () => this.pickTemplate(t.key)
    }, /*#__PURE__*/React.createElement("b", null, t.name), /*#__PURE__*/React.createElement("span", null, neutral(t.description)), /*#__PURE__*/React.createElement("span", {
      className: "orc-tmeta"
    }, (t.steps || []).filter(s => s.kind === "email").length, " emails \xB7 fill in the [[bracketed]] parts before sending"))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      role: "option",
      "aria-selected": !template,
      className: !template ? "on" : "",
      onClick: () => this.pickTemplate("")
    }, /*#__PURE__*/React.createElement("b", null, "Blank"), /*#__PURE__*/React.createElement("span", null, "Write the emails yourself."))) : null, chosenT && !programId ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Starts from the ", chosenT.name, " template as soon as this outreach is saved. ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: () => this.setState({
        template: ""
      })
    }, "Start blank instead")) : null, applyBusy ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Adding the template's emails\u2026") : null, applyErr ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note bad"
    }, applyErr) : null, this.status() === "Running" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning orc-notice"
    }, "This outreach is sending: pause it on its page to change the emails.") : null, stepErr ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger orc-notice"
    }, stepErr) : null, !rows.length ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "No emails yet. Start from a template, or add the first email and write it yourself.") : null, /*#__PURE__*/React.createElement("div", {
      className: "orc-rows"
    }, rows.map(r => {
      const s = r.s;
      const ph = placeholders(s);
      const isOpen = open === s.id;
      const isFirst = r.index === 0;
      const subj = s.subject ? tokenText(s.subject) : isFirst ? "No subject yet" : "Follow-up, same thread";
      return /*#__PURE__*/React.createElement("div", {
        key: s.id,
        id: `orc-step-${s.id}`,
        className: "orc-row" + (isOpen ? " open" : "") + (previewId === s.id || !previewId && isFirst ? " sel" : "")
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "orc-rowhead",
        "aria-expanded": isOpen,
        onClick: () => this.openEmail(s.id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "orc-day"
      }, "Day ", num(r.day)), /*#__PURE__*/React.createElement("b", {
        className: "orc-subj"
      }, subj, !isFirst && s.subject ? /*#__PURE__*/React.createElement("span", {
        className: "orc-thread"
      }, " \xB7 new thread") : null), dirtySteps[s.id] ? /*#__PURE__*/React.createElement("span", {
        className: "orc-saving"
      }, "saving\u2026") : null, ph.length ? /*#__PURE__*/React.createElement("span", {
        className: "orc-badge warn"
      }, ph.length, " part", ph.length === 1 ? "" : "s", " to fill in") : /*#__PURE__*/React.createElement("span", {
        className: "orc-badge ok"
      }, "\u2713 Ready")), isOpen ? /*#__PURE__*/React.createElement("div", {
        className: "orc-rowbody"
      }, !isFirst ? this.renderWait(r, locked) : null, this.renderEmail(s, isFirst, locked, ph), !locked ? /*#__PURE__*/React.createElement("div", {
        className: "orc-rowtools"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-textbtn small",
        disabled: !!stepBusy || r.index === 0,
        onClick: this.swapSteps(s, rows[r.index - 1] && rows[r.index - 1].s)
      }, "Move earlier"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-textbtn small",
        disabled: !!stepBusy || r.index === rows.length - 1,
        onClick: this.swapSteps(s, rows[r.index + 1] && rows[r.index + 1].s)
      }, "Move later"), /*#__PURE__*/React.createElement("span", {
        className: "ecc-spacer"
      }), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-textbtn small orc-danger",
        disabled: !!stepBusy,
        onClick: this.deleteEmail(s)
      }, stepBusy === s.id ? "Deleting…" : "Delete this email"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-btn",
        onClick: () => this.setState({
          open: null
        })
      }, "Done")) : null) : null);
    }), !locked && (programId || !rows.length) ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "orc-add",
      disabled: !!stepBusy || this.state.saveState === "saving",
      onClick: rows.length ? this.addFollowUp : () => this.pickTemplate("")
    }, stepBusy === "add" ? "Adding…" : rows.length ? "+ Add a follow-up" : "+ Add the first email") : null), steps.length ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, sequenceLabel(steps), ". A follow-up with no subject goes out as a reply in the same thread. Everything autosaves; nothing sends until it is approved and started.") : null);
  }

  /** The wait leading into a follow-up: "Goes out N (business) days after the previous email". */
  renderWait(r, locked) {
    const w = r.waits[r.waits.length - 1];
    if (!w) return /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Goes out right after the previous email.");
    const others = r.waits.slice(0, -1).reduce((a, x) => a + (Number(x.waitDays) || 0), 0);
    return /*#__PURE__*/React.createElement("div", {
      className: "orc-inline orc-wait"
    }, /*#__PURE__*/React.createElement("span", null, "Goes out"), /*#__PURE__*/React.createElement("input", {
      style: {
        ...small,
        width: 64
      },
      type: "number",
      min: "1",
      "aria-label": "Days after the previous email",
      disabled: locked,
      value: w.waitDays == null ? "" : w.waitDays,
      onChange: e => this.editStep(w.id, {
        waitDays: e.target.value === "" ? null : Number(e.target.value)
      })
    }), /*#__PURE__*/React.createElement("label", {
      className: "ecc-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: locked,
      checked: w.businessDays,
      onChange: e => this.editStep(w.id, {
        businessDays: e.target.checked
      })
    }), " business"), /*#__PURE__*/React.createElement("span", null, "days after the previous email", others ? ` (plus ${num(others)} more from an earlier wait)` : "", ", unless they reply first."));
  }
  /**
   * One email step: the subject (separate, as in Campaigns), the A/B switch (Email · A · B …), then THE email editor Campaigns and
   * Journeys use (Composer: sections, brand frame, images, products, buttons, AI drafting, server preview). A step written as
   * plain text opens as one text section; it is stored as a design on the first edit. Variant designs live in variants_json.
   */
  renderEmail(s, isFirst, locked, ph) {
    const {
      brand,
      editing,
      templates
    } = this.state;
    const vs = s.variants || [];
    const sel = vs.some(v => v.key === editing[s.id]) ? editing[s.id] : "";
    const vi = vs.findIndex(v => v.key === sel);
    const v = vi > -1 ? vs[vi] : null;
    const stepDesign = parseDesign(s.designJson) || textToDesign(s.bodyText);
    const design = v ? parseDesign(v.designJson) || stepDesign : stepDesign;
    const setVariants = list => this.editStep(s.id, {
      variants: list
    });
    const setDesign = d => {
      if (locked) return;
      const json = JSON.stringify(d);
      if (v) setVariants(vs.map((x, k) => k === vi ? {
        ...x,
        designJson: json
      } : x));else this.editStep(s.id, {
        designJson: json
      });
    };
    const addVariant = () => {
      const base = vs.length ? vs : [{
        key: "A",
        subject: "",
        bodyText: "",
        designJson: ""
      }];
      const next = {
        key: String.fromCharCode(65 + base.length),
        subject: "",
        bodyText: "",
        designJson: ""
      };
      setVariants(base.concat([next]));
      this.setState(st => ({
        editing: {
          ...st.editing,
          [s.id]: next.key
        }
      }));
    };
    const subject = v ? v.subject : s.subject;
    const extra = Array.from(new Set((templates || []).reduce((a, t) => a.concat(t.mergeFields || []), []))).filter(m => MERGE.indexOf(m) < 0);
    return /*#__PURE__*/React.createElement("div", {
      className: "or-composer"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-grid2"
    }, /*#__PURE__*/React.createElement("label", {
      className: "ecc-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-label"
    }, "Subject", v ? ` (version ${v.key}; blank uses the email's subject)` : isFirst ? "" : " (blank replies in the same thread)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      disabled: locked,
      value: subject || "",
      placeholder: v ? s.subject || "the email's subject" : isFirst ? "Quick question about {{company}}" : "Re: first subject",
      onChange: e => v ? setVariants(vs.map((x, k) => k === vi ? {
        ...x,
        subject: e.target.value
      } : x)) : this.editStep(s.id, {
        subject: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ecc-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-label"
    }, "Try two versions"), /*#__PURE__*/React.createElement("div", {
      className: "row nowrap"
    }, vs.length ? /*#__PURE__*/React.createElement(Segmented, {
      value: sel,
      onChange: k => this.setState(st => ({
        editing: {
          ...st.editing,
          [s.id]: k
        }
      })),
      options: [["", "Email"]].concat(vs.map(x => [x.key, `Version ${x.key}`]))
    }) : /*#__PURE__*/React.createElement("span", {
      className: "mini"
    }, "one version"), locked || vs.length >= 4 ? null : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle",
      onClick: addVariant
    }, vs.length ? "+ Version" : "Add a second version"), !locked && v ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle danger",
      onClick: () => {
        setVariants(vs.filter((_, k) => k !== vi));
        this.setState(st => ({
          editing: {
            ...st.editing,
            [s.id]: ""
          }
        }));
      }
    }, "Remove ", v.key) : null))), isFirst && !v && !locked ? /*#__PURE__*/React.createElement(SubjectSuggest, {
      kind: "custom",
      triggerType: "outreach",
      subject: s.subject,
      preheader: "",
      onUse: r => this.editStep(s.id, {
        subject: r.subject || s.subject
      })
    }) : null, vs.length ? /*#__PURE__*/React.createElement("p", {
      className: "ecc-note",
      style: {
        margin: "6px 0 0"
      }
    }, "Leads rotate through the versions; a version you leave as it is sends the email's own text. The winner is reported, never applied automatically.") : null, ph.length ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note warn",
      style: {
        margin: "8px 0 0"
      }
    }, "Fill in before sending: ", ph.join(", "), ". The checks refuse anything still in [[double brackets]].") : null, /*#__PURE__*/React.createElement("p", {
      className: "ecc-note",
      style: {
        margin: "8px 0 0"
      }
    }, "Personal details: ", MERGE.map(m => /*#__PURE__*/React.createElement("code", {
      key: m
    }, `{{${m}}}`)).reduce((a, x) => a.length ? a.concat([" · ", x]) : [x], []), "; a fallback goes after a bar, ", /*#__PURE__*/React.createElement("code", null, "{{first_name|there}}"), ".", extra.length ? /*#__PURE__*/React.createElement(React.Fragment, null, " Template fields: ", extra.join(", "), ".") : null, " Keep it short: one idea per email; images cost some deliverability. Your postal address and opt-out line are added to every email."), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement(Composer, {
      key: `${s.id}:${sel}`,
      design: design,
      brand: brand || {},
      kind: "custom",
      triggerType: "outreach",
      onChange: setDesign,
      onPickStarter: k => setDesign(starterDesign(k)),
      renderPreview: this.renderPreviewFor(s, subject)
    })), locked ? /*#__PURE__*/React.createElement("p", {
      className: "ecc-note"
    }, "Read only while this outreach is ", this.status() === "Running" ? "sending; pause it to edit" : statusLabel(this.status()).toLowerCase(), ".") : null);
  }
  /** Emails a day at full warmup (chosen mailboxes × their daily cap) and weekdays for the first email to reach every lead. */
  pace() {
    const {
      form,
      mailboxes,
      d,
      est
    } = this.state;
    const chosen = (mailboxes || []).filter(m => form.mailboxIds.indexOf(idStr(m.id)) > -1);
    let perDay = chosen.reduce((a, m) => a + (Number(m.dailyCap) || 0), 0);
    if (!perDay && est.res && Number(est.res.dailyCapacityAtFullRamp)) perDay = Number(est.res.dailyCapacityAtFullRamp);
    const counts = d && d.leads || {};
    const leads = Number(counts.active) || Number(counts.total) || 0;
    const days = perDay && leads ? Math.ceil(leads / perDay) : 0;
    return {
      chosen,
      perDay,
      leads,
      days
    };
  }
  renderHow() {
    const {
      form,
      mailboxes,
      panel,
      brand,
      est
    } = this.state;
    const locked = this.locked();
    const w = form.window,
      sr = form.stopRules,
      st = form.settings;
    const all = mailboxes || [];
    const {
      chosen,
      perDay,
      leads,
      days
    } = this.pace();
    const weekdaysOnly = (w.days || []).every(x => Number(x) <= 5);
    const unit = weekdaysOnly ? "weekday" : "sending day";
    const brandAddr = brand && brand.mailingAddress ? String(brand.mailingAddress).trim() : "";
    const e = est.res;
    let paceLine;
    if (!chosen.length) paceLine = "Choose the mailboxes it sends from to see how many emails go out a day.";else if (!perDay) paceLine = "The chosen mailboxes have no daily limit set yet.";else if (!leads) paceLine = `About ${num(perDay)} emails a day once the mailboxes have warmed up. Load the leads to see how long the first email takes to reach everyone.`;else paceLine = `About ${num(perDay)} emails a day once the mailboxes have warmed up, so the first email reaches everyone in about ${num(days)} ${unit}${days === 1 ? "" : "s"}.`;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "ecc-grid2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-label"
    }, "From"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "orc-select",
      "aria-expanded": !!panel.from,
      disabled: mailboxes === null,
      onClick: () => this.togglePanel("from")
    }, mailboxes === null ? "Loading mailboxes…" : !all.length ? "No mailboxes yet" : `${num(chosen.length)} of ${num(all.length)} mailbox${all.length === 1 ? "" : "es"}`, " ", /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true"
    }, "\u25BE"))), /*#__PURE__*/React.createElement("div", {
      className: "ecc-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-label"
    }, "When"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "orc-select",
      "aria-expanded": !!panel.when,
      onClick: () => this.togglePanel("when")
    }, windowText(w), " ", /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true"
    }, "\u25BE")))), panel.from ? /*#__PURE__*/React.createElement("div", {
      className: "orc-panel"
    }, !all.length ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "No outreach mailboxes yet. Add sending domains and mailboxes under ", /*#__PURE__*/React.createElement(Link, {
      to: "/Email/senders/outreach"
    }, "Email settings, Outreach mailboxes"), "; a mailbox warms up for about three weeks before it sends.") : /*#__PURE__*/React.createElement("div", {
      className: "orc-mblist"
    }, all.map(m => {
      const id = idStr(m.id);
      const on = form.mailboxIds.indexOf(id) > -1;
      const off = locked || m.status === "disabled";
      return /*#__PURE__*/React.createElement("label", {
        key: id,
        className: "orc-mb" + (on ? " on" : "") + (off ? " off" : "")
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: on,
        disabled: off,
        onChange: () => !off && this.toggleMailbox(id)
      }), /*#__PURE__*/React.createElement("span", {
        className: "orc-mb-text"
      }, /*#__PURE__*/React.createElement("b", null, m.address), /*#__PURE__*/React.createElement("span", null, m.displayName || "no display name", m.hasToken ? "" : " · not connected")), /*#__PURE__*/React.createElement("span", {
        className: "orc-mb-status"
      }, /*#__PURE__*/React.createElement(Pill, {
        tone: mailboxTone(m.status)
      }, m.status), m.status === "warming" ? /*#__PURE__*/React.createElement("span", {
        className: "mini"
      }, " day ", num(m.warmDay)) : m.status === "provisioning" ? /*#__PURE__*/React.createElement("span", {
        className: "mini"
      }, " warmup not started") : m.pausedReason ? /*#__PURE__*/React.createElement("span", {
        className: "mini"
      }, " ", m.pausedReason) : null), /*#__PURE__*/React.createElement("span", {
        className: "orc-mb-cap"
      }, num(m.sentToday), " sent today \xB7 ", num(m.capToday), " today \xB7 ", num(m.dailyCap), " a day"));
    })), /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Sends one email at a time from your own warmed mailboxes, never from your newsletter sender. Each lead stays with the mailbox that sent the first email, so follow-ups land in the same thread. Starting needs every chosen mailbox warmed up. ", /*#__PURE__*/React.createElement(Link, {
      to: "/Email/senders/outreach"
    }, "Manage outreach mailboxes"))) : null, panel.when ? /*#__PURE__*/React.createElement("div", {
      className: "orc-panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-seg",
      role: "group",
      "aria-label": "Days"
    }, DAYS.map(([n, l]) => {
      const on = (w.days || []).indexOf(n) > -1;
      return /*#__PURE__*/React.createElement("button", {
        key: n,
        type: "button",
        className: on ? "on" : "",
        "aria-pressed": on,
        disabled: locked,
        onClick: () => this.setForm("window", {
          days: on ? w.days.filter(x => x !== n) : w.days.concat([n]).sort()
        })
      }, l);
    })), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "from"), /*#__PURE__*/React.createElement("input", {
      style: {
        ...small,
        width: 110
      },
      type: "time",
      "aria-label": "From",
      disabled: locked,
      value: w.start,
      onChange: ev => this.setForm("window", {
        start: ev.target.value
      })
    }), /*#__PURE__*/React.createElement("span", null, "to"), /*#__PURE__*/React.createElement("input", {
      style: {
        ...small,
        width: 110
      },
      type: "time",
      "aria-label": "To",
      disabled: locked,
      value: w.end,
      onChange: ev => this.setForm("window", {
        end: ev.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "Time zone"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["recipient", "Each person's own time zone"], ["America/New_York", "America/New_York"], ["America/Chicago", "America/Chicago"], ["America/Denver", "America/Denver"], ["America/Los_Angeles", "America/Los_Angeles"]],
      value: w.tz,
      onChange: x => this.setForm("window", {
        tz: x
      }),
      width: "280px"
    })), w.tz === "recipient" ? /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "When a lead's time zone is unknown"), /*#__PURE__*/React.createElement("input", {
      style: {
        ...FIELD,
        width: 220
      },
      disabled: locked,
      value: w.defaultTz || "",
      onChange: ev => this.setForm("window", {
        defaultTz: ev.target.value
      })
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, windowLabel(w))) : null, /*#__PURE__*/React.createElement("span", {
      className: "ecc-note"
    }, paceLine), e && (e.warnings || []).length ? /*#__PURE__*/React.createElement("ul", {
      className: "ec-notes"
    }, e.warnings.map((x, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, x))) : null, /*#__PURE__*/React.createElement("div", {
      className: "orc-disclosures"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-disclosure",
      "aria-expanded": !!panel.more,
      onClick: () => this.togglePanel("more")
    }, panel.more ? "▾" : "▸", " More sending options"), e ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-disclosure",
      "aria-expanded": !!panel.runway,
      onClick: () => this.togglePanel("runway")
    }, panel.runway ? "▾" : "▸", " See the full estimate") : null), panel.runway && e ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-breakdown"
    }, /*#__PURE__*/React.createElement("div", {
      className: "or-kv"
    }, /*#__PURE__*/React.createElement("span", null, "Leads"), /*#__PURE__*/React.createElement("b", null, num(e.leads), " ", /*#__PURE__*/React.createElement("small", null, num(e.activeLeads), " active")), /*#__PURE__*/React.createElement("span", null, "Emails"), /*#__PURE__*/React.createElement("b", null, num(e.emailSteps)), /*#__PURE__*/React.createElement("span", null, "Sends"), /*#__PURE__*/React.createElement("b", null, num(e.totalSends), " ", /*#__PURE__*/React.createElement("small", null, num(e.remainingSends), " remaining")), /*#__PURE__*/React.createElement("span", null, "Mailboxes"), /*#__PURE__*/React.createElement("b", null, num(e.mailboxCount), " ", /*#__PURE__*/React.createElement("small", null, num(e.activeMailboxCount), " warmed up \xB7 ", num(e.warmingMailboxCount), " warming")), /*#__PURE__*/React.createElement("span", null, "Emails a day"), /*#__PURE__*/React.createElement("b", null, num(e.dailyCapacityNow), " now ", /*#__PURE__*/React.createElement("small", null, num(e.dailyCapacityAtFullRamp), " once warmed up")), /*#__PURE__*/React.createElement("span", null, "Warmup left"), /*#__PURE__*/React.createElement("b", null, num(e.warmupDaysRemaining), " days"), /*#__PURE__*/React.createElement("span", null, "First email done in"), /*#__PURE__*/React.createElement("b", null, e.firstStepDays ? `${num(e.firstStepDays)} days` : "-"), /*#__PURE__*/React.createElement("span", null, "All emails done in"), /*#__PURE__*/React.createElement("b", null, e.runwayDays ? `${num(e.runwayDays)} days` : "-", e.projectedCompletion ? /*#__PURE__*/React.createElement("small", null, " ends ", dateOnly(e.projectedCompletion)) : null)), /*#__PURE__*/React.createElement("div", {
      className: "ecc-note",
      style: {
        marginTop: 6
      }
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: this.estimate
    }, "Estimate again"))) : null, panel.more ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-more"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-more-block"
    }, /*#__PURE__*/React.createElement("b", null, "Stop the emails to a lead when"), [["onReply", "They reply"], ["onClick", "They click a link"], ["onBounce", "An email bounces (and never email them again)"], ["onUnsub", "They opt out (and never email them again)"], ["onWrongPerson", "They say they are the wrong person"]].map(([k, l]) => /*#__PURE__*/React.createElement("label", {
      key: k,
      className: "ecc-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: locked,
      checked: !!sr[k],
      onChange: ev => this.setForm("stopRules", {
        [k]: ev.target.checked
      })
    }), " ", l)), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "Out of office replies"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["reschedule", "Wait until they are back"], ["stop", "Stop the emails"], ["ignore", "Ignore"]],
      value: sr.onOOO,
      onChange: x => this.setForm("stopRules", {
        onOOO: x
      }),
      width: "260px"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ecc-more-block"
    }, /*#__PURE__*/React.createElement("b", null, "Every email carries"), /*#__PURE__*/React.createElement("label", {
      className: "ecc-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-label"
    }, "Postal address (required)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      disabled: locked,
      value: st.physicalAddress || "",
      placeholder: "Company, street, city, state ZIP",
      onChange: ev => this.setForm("settings", {
        physicalAddress: ev.target.value
      })
    })), !locked && brandAddr && brandAddr !== (st.physicalAddress || "").trim() ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: () => this.setForm("settings", {
        physicalAddress: brandAddr
      })
    }, "Use the brand kit address"), " (", brandAddr, ")") : null, /*#__PURE__*/React.createElement("label", {
      className: "ecc-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-label"
    }, "Opt-out line (", "{url}", " becomes the opt-out link)"), /*#__PURE__*/React.createElement("input", {
      style: INPUT,
      disabled: locked,
      value: st.optOutLine || "",
      onChange: ev => this.setForm("settings", {
        optOutLine: ev.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "Legal basis"), /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["legitimate_interest", "Legitimate interest (US B2B)"], ["implied_consent", "Implied consent (CASL, published business address)"], ["express_consent", "Express consent"]],
      value: form.legalBasis,
      onChange: x => this.setForm(null, {
        legalBasis: x
      }),
      width: "320px"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ecc-more-block"
    }, /*#__PURE__*/React.createElement("b", null, "Pace and limits"), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "Space emails"), /*#__PURE__*/React.createElement("input", {
      style: small,
      type: "number",
      min: "1",
      "aria-label": "Minimum minutes apart",
      disabled: locked,
      value: st.jitterMin == null ? "" : st.jitterMin,
      onChange: ev => this.setForm("settings", {
        jitterMin: ev.target.value
      })
    }), /*#__PURE__*/React.createElement("span", null, "to"), /*#__PURE__*/React.createElement("input", {
      style: small,
      type: "number",
      min: "1",
      "aria-label": "Maximum minutes apart",
      disabled: locked,
      value: st.jitterMax == null ? "" : st.jitterMax,
      onChange: ev => this.setForm("settings", {
        jitterMax: ev.target.value
      })
    }), /*#__PURE__*/React.createElement("span", null, "minutes apart per mailbox")), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "Pause at a bounce rate of"), /*#__PURE__*/React.createElement("input", {
      style: small,
      type: "number",
      step: "0.005",
      min: "0",
      max: "1",
      "aria-label": "Pause at bounce rate",
      disabled: locked,
      value: st.pauseBounceRate == null ? "" : st.pauseBounceRate,
      placeholder: "default",
      onChange: ev => this.setForm("settings", {
        pauseBounceRate: ev.target.value
      })
    }), /*#__PURE__*/React.createElement("span", null, "(0.05 = 5 %; blank uses the standard limit)")), /*#__PURE__*/React.createElement("div", {
      className: "orc-inline"
    }, /*#__PURE__*/React.createElement("span", null, "Refuse an audience with more than"), /*#__PURE__*/React.createElement("input", {
      style: small,
      type: "number",
      step: "0.01",
      min: "0",
      max: "1",
      "aria-label": "Largest consumer share",
      disabled: locked,
      value: st.maxConsumerShare == null ? "" : st.maxConsumerShare,
      onChange: ev => this.setForm("settings", {
        maxConsumerShare: ev.target.value
      })
    }), /*#__PURE__*/React.createElement("span", null, "consumers (0.10 = 10 %)")), /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Daily limits are per mailbox and rise as it warms up; change them in ", /*#__PURE__*/React.createElement(Link, {
      to: "/Email/senders/outreach"
    }, "Outreach mailboxes"), "."), /*#__PURE__*/React.createElement("label", {
      className: "ecc-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: locked,
      checked: st.plainText !== false,
      onChange: ev => this.setForm("settings", {
        plainText: ev.target.checked
      })
    }), " Plain text only"), this.state.steps.some(x => x.kind === "email" && parseDesign(x.designJson)) ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Applies to emails written as plain text. Emails built with sections in the editor always send formatted, with a plain-text copy.") : null), this.renderTracking(st, locked)) : null);
  }

  /**
   * Click and open tracking. Both go through the sending domain's own tracking address (link.<domain>), never a shared
   * host. Opens need a formatted email (the pixel is an image), so "Plain text only" turns them off.
   */
  renderTracking(st, locked) {
    const all = this.state.mailboxes || [];
    const chosen = all.filter(m => this.state.form.mailboxIds.indexOf(idStr(m.id)) > -1);
    const domainOf = a => String(a || "").split("@")[1] || "";
    const first = chosen[0] || all[0];
    const host = first && first.trackingHost || (first && domainOf(first.address) ? `link.${domainOf(first.address)}` : "link.<your domain>");
    const missing = Array.from(new Set(chosen.filter(m => !m.trackingHost).map(m => domainOf(m.address)).filter(Boolean)));
    const plain = st.plainText !== false;
    return /*#__PURE__*/React.createElement("div", {
      className: "ecc-more-block orc-tracking"
    }, /*#__PURE__*/React.createElement("b", null, "Tracking"), /*#__PURE__*/React.createElement("label", {
      className: "ecc-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: locked,
      checked: !!st.trackClicks,
      onChange: ev => this.setForm("settings", {
        trackClicks: ev.target.checked
      })
    }), " Track link clicks"), /*#__PURE__*/React.createElement("div", {
      className: "ecc-note orc-sub"
    }, "Links go through ", host, " so clicks can be counted."), /*#__PURE__*/React.createElement("label", {
      className: "ecc-check" + (plain ? " off" : "")
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: locked || plain,
      checked: !plain && !!st.trackOpens,
      onChange: ev => this.setForm("settings", {
        trackOpens: ev.target.checked
      })
    }), " Track opens"), plain ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note orc-sub"
    }, "Opens can only be counted in formatted emails. Turn off \"Plain text only\" above to use this.") : /*#__PURE__*/React.createElement("div", {
      className: "ecc-note warn orc-sub"
    }, "Adds a tiny invisible image. Some inboxes treat that as a marketing sign, and Apple Mail and company filters open emails automatically, so opens can be overcounted. Replies stay the best measure."), missing.length && (st.trackClicks || !plain && st.trackOpens) ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note orc-sub"
    }, missing.join(", "), " ", missing.length === 1 ? "has" : "have", " no tracking address yet. Clicks", !plain && st.trackOpens ? " and opens" : "", " from ", missing.length === 1 ? "it" : "them", " start counting once its tracking address is ready.") : null);
  }
  /** The old Review step's checks as one list: [{state: ok|warn|bad, text, fix}]. */
  checks() {
    const {
      d,
      lint,
      steps
    } = this.state;
    const st = d && d.settings || this.state.form.settings || {};
    const counts = d && d.leads || {};
    const mbs = d && d.mailboxes || [];
    const notActive = mbs.filter(m => m.status !== "active");
    const emailSteps = steps.filter(s => s.kind === "email");
    const w = this.state.form.window;
    const fix = (n, label) => ({
      label: label || "Fix it",
      onClick: () => this.goTo(n)
    });
    const out = [];
    if (!this.state.programId) out.push({
      state: "warn",
      text: "Name this outreach so it can be saved.",
      fix: {
        label: "Add a name",
        onClick: () => {
          const el = document.querySelector(".orc-view .ecc-name");
          if (el) el.focus();
        }
      }
    });
    out.push(counts.total > 0 ? {
      state: "ok",
      text: `${num(counts.total)} business contact${counts.total === 1 ? "" : "s"} loaded${counts.unverified ? ` (${num(counts.unverified)} not checked in the last 90 days)` : ""}`
    } : {
      state: "bad",
      text: "No leads loaded yet.",
      fix: fix(1)
    });
    out.push(emailSteps.length ? {
      state: "ok",
      text: `${sequenceLabel(steps).replace(/^./, c => c.toUpperCase())}`
    } : {
      state: "bad",
      text: "No emails yet.",
      fix: fix(2)
    });
    out.push({
      state: "ok",
      text: trackingText(st)
    });
    const nPh = emailSteps.reduce((a, s) => a + placeholders(s).length, 0);
    if (nPh) out.push({
      state: "warn",
      text: `${num(nPh)} part${nPh === 1 ? "" : "s"} still to fill in.`,
      fix: {
        label: "Show me",
        onClick: this.showPlaceholders
      }
    });
    out.push(!mbs.length ? {
      state: "bad",
      text: "No mailboxes chosen.",
      fix: fix(3)
    } : notActive.length ? {
      state: "warn",
      text: `${num(notActive.length)} of ${num(mbs.length)} mailboxes are not warmed up yet (${notActive.map(m => `${m.address}: ${m.status}${m.status === "warming" ? ` day ${m.warmDay}` : ""}`).join(", ")}). Starting needs every chosen mailbox warmed up.`
    } : {
      state: "ok",
      text: `${num(mbs.length)} warmed-up mailbox${mbs.length === 1 ? "" : "es"}`
    });
    const windowOk = (w.days || []).length > 0 && String(w.start || "") < String(w.end || "");
    out.push(windowOk ? {
      state: "ok",
      text: `Sends ${windowText(w).replace(/^./, c => c.toLowerCase())}`
    } : {
      state: "bad",
      text: "Pick the days and hours it sends.",
      fix: {
        label: "Fix it",
        onClick: () => {
          this.togglePanel("when", true);
          this.goTo(3);
        }
      }
    });
    out.push(st.physicalAddress ? {
      state: "ok",
      text: "Postal address and opt-out line on every email"
    } : {
      state: "bad",
      text: "Add your postal address.",
      fix: {
        label: "Add it",
        onClick: () => {
          this.togglePanel("more", true);
          this.goTo(3);
        }
      }
    });
    const findings = lint.res && lint.res.findings || [];
    const errors = findings.filter(f => f.severity === "error").length;
    const lintFix = {
      label: lint.busy ? "Checking…" : "Check again",
      onClick: this.lint
    };
    if (!this.state.programId) {/* nothing to check yet */} else if (lint.err) out.push({
      state: "warn",
      text: lint.err,
      fix: lintFix
    });else if (lint.res) out.push(lint.res.clean ? {
      state: findings.length ? "warn" : "ok",
      text: findings.length ? `Checks pass with ${num(findings.length)} warning${findings.length === 1 ? "" : "s"}.` : "Subject, opt-out, address, spam phrases and images checked",
      fix: lintFix
    } : {
      state: "bad",
      text: `${num(errors)} problem${errors === 1 ? "" : "s"} to fix before submitting.`,
      fix: lintFix
    });else out.push({
      state: "warn",
      text: lint.busy ? "Checking…" : "Not checked yet.",
      fix: lint.busy ? null : {
        label: "Check now",
        onClick: this.lint
      }
    });
    if (this.state.programId) out.push(st.testSent ? {
      state: "ok",
      text: "A test went out.",
      fix: {
        label: "Send another",
        onClick: () => this.setState({
          test: {
            ...this.state.test,
            open: true
          }
        })
      }
    } : {
      state: "warn",
      text: "No test sent yet.",
      fix: {
        label: "Send a test",
        onClick: () => this.setState({
          test: {
            ...this.state.test,
            open: true
          }
        })
      }
    });
    return out;
  }
  renderChecklist() {
    const {
      d,
      lint,
      test,
      steps,
      submit
    } = this.state;
    const p = d && d.program || {
      status: "Draft"
    };
    const mbs = d && d.mailboxes || [];
    const notActive = mbs.filter(m => m.status !== "active");
    const findings = lint.res && lint.res.findings || [];
    const emailRows = this.emailRows();
    const others = (d && d.nextTransitions || []).filter(t => t !== "InReview");
    return /*#__PURE__*/React.createElement("div", {
      ref: this.sec.checks,
      className: "ecc-card ecc-checklist" + (this.state.flash === "checks" ? " orc-flash" : "")
    }, /*#__PURE__*/React.createElement("b", {
      className: "ecc-checklist-title"
    }, "Before it goes for review"), this.checks().map((k, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "ecc-checkrow " + (k.state === "bad" ? "warn orc-bad" : k.state)
    }, /*#__PURE__*/React.createElement("span", {
      className: "m",
      "aria-hidden": "true"
    }, k.state === "ok" ? "✓" : "!"), /*#__PURE__*/React.createElement("span", null, k.text, k.fix ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: k.fix.onClick
    }, k.fix.label)) : null))), findings.length ? /*#__PURE__*/React.createElement("ul", {
      className: "ecc-findings"
    }, findings.filter(f => !/physical(\s|)address|postal address/i.test(String(f.message || ""))).map((f, i) => {
      const row = f.stepOrdinal ? emailRows.find(r => r.s.ordinal === Number(f.stepOrdinal)) : null;
      return /*#__PURE__*/React.createElement("li", {
        key: i,
        className: f.severity
      }, /*#__PURE__*/React.createElement("span", {
        className: "ecc-sev " + f.severity
      }, f.severity === "error" ? "Problem" : f.severity === "warning" ? "Warning" : "Note"), " ", plainFinding(f.message), " ", row ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-link",
        onClick: () => this.setState({
          open: row.s.id,
          previewId: row.s.id
        }, () => this.goTo(2, document.getElementById(`orc-step-${row.s.id}`)))
      }, "Show me") : null);
    })) : null, test.open ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-testform"
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        ...FIELD,
        flex: "1 1 180px"
      },
      value: test.to,
      "aria-label": "Send the test to",
      placeholder: "Send the test to",
      onChange: ev => this.setState({
        test: {
          ...test,
          to: ev.target.value
        }
      })
    }), /*#__PURE__*/React.createElement("select", {
      style: {
        ...FIELD,
        width: 120
      },
      "aria-label": "Which email",
      value: test.stepOrdinal,
      onChange: ev => this.setState({
        test: {
          ...test,
          stepOrdinal: ev.target.value
        }
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "First email"), emailRows.slice(1).map(r => /*#__PURE__*/React.createElement("option", {
      key: r.s.id,
      value: r.s.ordinal
    }, "Day ", r.day, " email"))), /*#__PURE__*/React.createElement("select", {
      style: {
        ...FIELD,
        width: 180
      },
      "aria-label": "From mailbox",
      value: test.mailboxId,
      onChange: ev => this.setState({
        test: {
          ...test,
          mailboxId: ev.target.value
        }
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Any chosen mailbox"), mbs.map(m => /*#__PURE__*/React.createElement("option", {
      key: idStr(m.id),
      value: idStr(m.id)
    }, m.address))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: test.busy,
      onClick: this.sendTest
    }, test.busy ? "Sending…" : "Send test"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn",
      disabled: test.busy,
      onClick: () => this.setState({
        test: {
          ...test,
          open: false,
          err: null
        }
      })
    }, "Cancel")) : null, test.err ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note bad"
    }, test.err, test.preview ? /*#__PURE__*/React.createElement("pre", {
      className: "or-pre"
    }, test.preview) : null) : null, test.res ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Test sent from ", test.res.mailbox, ": \"", test.res.subject, "\".", (test.res.missingMergeFields || []).length ? /*#__PURE__*/React.createElement(React.Fragment, null, " Missing personal details: ", test.res.missingMergeFields.join(", "), ".") : null) : null, p.status === "InReview" ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate"
    }, "Submitted by ", who(p.submittedBy, "a teammate"), ". An administrator approves or sends it back; editing is paused meanwhile.") : p.status === "Approved" ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate ok"
    }, "Approved by ", who(p.reviewedBy, "an administrator"), ". ", notActive.length ? "Starting unlocks once every chosen mailbox has warmed up." : "Ready to start.") : p.reviewStatus === "Rejected" ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate bad"
    }, /*#__PURE__*/React.createElement("b", null, "Sent back"), p.reviewedBy ? ` by ${who(p.reviewedBy, "an administrator")}` : "", ": ", p.rejectReason || "no reason given", ". Fix it and submit again.") : p.status === "Draft" ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate"
    }, "An administrator approves outreach before it sends. Submit it when the checks pass; once approved, you start it.") : null, submit.err ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger",
      style: {
        margin: 0
      }
    }, submit.err) : submit.ok ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, submit.ok) : null, d && d.program && others.length ? /*#__PURE__*/React.createElement(Lifecycle, {
      program: p,
      next: others,
      blocked: notActive.length && p.status !== "Paused" ? {
        Running: `${num(notActive.length)} of ${num(mbs.length)} chosen mailboxes are still warming up.`
      } : null,
      onDone: r => {
        if (r && r.status === "Running") {
          this.props.history.push(`/Email/campaigns/outreach/${encodeURIComponent(p.id)}`);
          return;
        }
        this.reload();
        this.runChecks();
      }
    }) : null, steps.length && this.state.programId ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, /*#__PURE__*/React.createElement(Link, {
      to: `/Email/campaigns/outreach/${encodeURIComponent(this.state.programId)}`
    }, "Open the outreach page")) : null);
  }

  // ---------------------------------------------------------------- right column: preview

  previewRow() {
    const rows = this.emailRows();
    return rows.find(r => r.s.id === this.state.previewId) || rows[0] || null;
  }
  renderPreview() {
    const {
      leads,
      leadIdx,
      form,
      mailboxes,
      steps
    } = this.state;
    const row = this.previewRow();
    if (!row) return /*#__PURE__*/React.createElement("div", {
      className: "ecc-card orc-preview"
    }, /*#__PURE__*/React.createElement("div", {
      className: "orc-prev-head"
    }, /*#__PURE__*/React.createElement("b", null, "Preview")), /*#__PURE__*/React.createElement("div", {
      className: "orc-prev-body"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-note"
    }, "Add an email to see it here, as a lead will see it.")));
    const items = leads && leads.items || [];
    const lead = items.length ? items[leadIdx % items.length] : null;
    const mb = (mailboxes || []).find(m => form.mailboxIds.indexOf(idStr(m.id)) > -1);
    const vals = {
      ...(lead ? parseMaybe(lead.mergeJson) || {} : {})
    };
    if (lead && !vals.email) vals.email = lead.email;
    if (mb && mb.displayName) {
      if (!vals.sender_name) vals.sender_name = mb.displayName;
      if (!vals.rep_name) vals.rep_name = mb.displayName;
    }
    if (vals.first_name && !vals.FirstName) vals.FirstName = vals.first_name;
    const firstEmail = steps.find(s => s.kind === "email");
    const subjRaw = row.s.subject || (row.index > 0 && firstEmail ? `Re: ${firstEmail.subject || ""}` : "");
    const subject = fillMerge(subjRaw, vals);
    const paras = emailParas(row.s.designJson, row.s.bodyText).map(p => fillMerge(p, vals));
    const name = lead ? vals.first_name || lead.email : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "ecc-card orc-preview"
    }, /*#__PURE__*/React.createElement("div", {
      className: "orc-prev-head"
    }, /*#__PURE__*/React.createElement("b", null, "Day ", num(row.day), ", ", name ? `as ${name} will see it` : "with sample details"), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), items.length > 1 ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn small",
      onClick: () => this.setState({
        leadIdx: (leadIdx + 1) % items.length
      })
    }, "Next lead \u203A") : null), /*#__PURE__*/React.createElement("div", {
      className: "orc-prev-body"
    }, /*#__PURE__*/React.createElement("span", {
      className: "orc-prev-meta"
    }, "From ", mb ? mb.address : "your mailbox", lead ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 To ", lead.email) : null), /*#__PURE__*/React.createElement("b", {
      className: "orc-prev-subj"
    }, subject ? marked(subject, "s") : /*#__PURE__*/React.createElement("span", {
      className: "orc-dim"
    }, "No subject yet")), paras.length ? paras.map((p, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "orc-prev-p"
    }, marked(p, `p${i}`))) : /*#__PURE__*/React.createElement("span", {
      className: "orc-dim"
    }, "This email is empty.")), /*#__PURE__*/React.createElement("div", {
      className: "orc-prev-foot ecc-note"
    }, "Your postal address and opt-out line are added below. The editor's preview shows the exact email."));
  }
  section(n, title, right, body) {
    return /*#__PURE__*/React.createElement("section", {
      ref: this.sec[n],
      className: "ecc-section" + (this.state.flash === n ? " flash" : ""),
      "aria-labelledby": `orc-sec-${n}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-section-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-num",
      "aria-hidden": "true"
    }, n), /*#__PURE__*/React.createElement("b", {
      id: `orc-sec-${n}`
    }, title), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), right), body);
  }

  // ---------------------------------------------------------------- page

  render() {
    if (!hasTenantFeature("outreach") || this.state.notEnabled) return /*#__PURE__*/React.createElement(NotEnabled, null);
    const {
      d,
      programId,
      form,
      loadErr,
      saveState,
      savedAt,
      saveErr,
      formDirty,
      dirtySteps,
      steps,
      load,
      test,
      submit,
      templates,
      nameErr
    } = this.state;
    if (loadErr) return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view ec-view or-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Email/outreach"
      }, "Email"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Email/outreach"
      }, "Outreach")),
      title: "Outreach"
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, loadErr));
    if (!form || programId && !d && saveState !== "saving") return /*#__PURE__*/React.createElement(Loader, null);
    const p = d && d.program || {
      status: "Draft"
    };
    const locked = this.locked();
    const dirty = formDirty || Object.keys(dirtySteps).length > 0;
    const saveText = saveState === "saving" ? "saving…" : saveState === "error" ? "couldn't save" : !programId ? form.name.trim() ? "not saved yet" : "name it to save" : dirty ? "unsaved changes" : saveState === "saved" ? ago(savedAt) === "just now" ? "saved a moment ago" : `saved ${ago(savedAt)}` : "saved";
    const counts = d && d.leads || {};
    const canSubmit = !!(d && d.program && (d.nextTransitions || []).indexOf("InReview") > -1);
    const leadCount = load.busy ? /*#__PURE__*/React.createElement("span", {
      className: "ecc-count pending"
    }, "Loading leads\u2026") : counts.total ? /*#__PURE__*/React.createElement("span", {
      className: "ecc-count"
    }, num(counts.total), " lead", counts.total === 1 ? "" : "s") : null;
    const tplBtn = !this.stepsLocked() && !steps.length && (templates || []).length ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn small",
      "aria-expanded": !!this.state.panel.templates,
      onClick: () => this.togglePanel("templates")
    }, "Start from a template") : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view ec-view ecc-view or-view orc-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-back",
      "aria-label": "Back to Outreach",
      onClick: this.back
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M10 3L5 8l5 5",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ecc-titleblock"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ecc-name",
      value: form.name,
      disabled: locked,
      placeholder: "Name this outreach",
      "aria-label": "Outreach name",
      autoFocus: !programId,
      onChange: e => this.setForm(null, {
        name: e.target.value
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "ecc-meta" + (nameErr ? " orc-meta-err" : "")
    }, nameErr || /*#__PURE__*/React.createElement(React.Fragment, null, "Outreach \xB7 ", statusLabel(p.status).toLowerCase(), " \xB7 ", saveText, saveState === "error" ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: this.save
    }, "Try again")) : null, programId ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: `/Email/campaigns/outreach/${encodeURIComponent(programId)}`
    }, "Outreach page")) : null))), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), programId && !locked ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn",
      disabled: test.busy,
      onClick: this.sendTestToMe
    }, test.busy ? "Sending…" : "Send me a test") : null, canSubmit ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: !!submit.busy,
      onClick: this.submit
    }, submit.busy ? "Submitting…" : "Submit for review") : !programId ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: true,
      title: "Name it and finish the checks first"
    }, "Submit for review") : null), locked && d ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning ecc-banner"
    }, /*#__PURE__*/React.createElement("b", null, "This outreach is ", p.status === "InReview" ? "in review" : statusLabel(p.status).toLowerCase(), "."), " ", p.status === "InReview" ? "Editing is paused until an administrator approves it or sends it back." : "It can no longer be edited.", " ", /*#__PURE__*/React.createElement(Link, {
      to: `/Email/campaigns/outreach/${encodeURIComponent(programId)}`
    }, "Open the outreach")) : saveState === "error" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger ecc-banner"
    }, saveErr, " ", /*#__PURE__*/React.createElement("a", {
      onClick: this.save
    }, "Try again")) : null, p.status === "Approved" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice ecc-banner"
    }, "Approved. Changes made now are saved but not reviewed again.") : null, /*#__PURE__*/React.createElement("div", {
      className: "ecc-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-main"
    }, this.section(1, "Who it goes to", leadCount, this.renderWho()), this.section(2, "What it says", tplBtn, this.renderSays()), this.section(3, "How it sends", null, this.renderHow())), /*#__PURE__*/React.createElement("aside", {
      className: "ecc-side"
    }, this.renderPreview(), this.renderChecklist())));
  }
}
export default withRouter(OutreachWizardComponent);